// EVE-Profile pure functions. 1:1 portiert aus V1 useEveProfile.js.
// - DEFAULT_PROFILE
// - mergeWithDefaults
// - formatEveProfilePromptBlock
// - parseAllAssets

import type { EveProfile } from '@/types/eve-profile';

export const DEFAULT_PROFILE: EveProfile = {
  tone: 'direkt',
  language: 'de',
  preferredAssets: ['BTC', 'ETH', 'SOL'],
  customAssets: '',
  maxLeverage: 3,
  riskPercent: 2.0,
  preferredTimeframes: ['4H'],
  tradingStyles: ['swing'],
  activeAgents: [
    'morgenbriefing',
    'marktupdate',
    'breaking',
    'lagebild',
    'moneyflow',
    'wochenendrückblick',
  ],
  agentSchedule: {
    morgenbriefing: 'manual',
    marktupdate: 'manual',
    breaking: 'manual',
    lagebild: 'manual',
    wochenendrückblick: 'manual',
  },
  agentTime: {
    morgenbriefing: '07:00',
    marktupdate: '12:00',
    breaking: '',
    lagebild: '20:00',
    wochenendrückblick: '18:00',
  },
  contextPortfolio: true,
  contextTrades: true,
  contextNews: true,
  contextMacro: true,
  contextMemory: true,
  contextRisk: true,
  contextEntity: true,
  contextFunding: true,
  contextSentiment: true,
  contextPolymarket: false,
  contextCot: false,
  freeContext: '',
  notifications: { whale: true, wallet: true, calls: true },
  personaMode: 'public',
};

/** Merged eine partial-Profile-Variante mit den Defaults. */
export function mergeWithDefaults(partial: Partial<EveProfile> | null | undefined): EveProfile {
  if (!partial || typeof partial !== 'object') return { ...DEFAULT_PROFILE };
  return { ...DEFAULT_PROFILE, ...partial };
}

const TONE_MAP: Record<string, string> = {
  direkt: 'Schreib knapp, direkt, kein unnötiges Ausschmücken. Fakten first.',
  analytisch: 'Analytisch und strukturiert. Zeige deine Denkschritte. Keine voreiligen Schlüsse.',
  entspannt: 'Entspannt und gesprächig, aber substanziell. Humor erlaubt.',
};

/**
 * Liefert eine zusammengeführte Asset-Liste (preferredAssets + customAssets).
 * V1-Verhalten: customAssets ist eine komma-separierte Liste, jeder Eintrag wird
 * upper-cased und getrimmt; leere Strings werden gefiltert.
 */
export function parseAllAssets(profile: EveProfile): string[] {
  const custom = (profile.customAssets ?? '')
    .split(',')
    .map((a) => a.trim().toUpperCase())
    .filter(Boolean);
  return [...(profile.preferredAssets ?? []), ...custom];
}

/**
 * V1 `getEveProfilePromptBlock` — ASCII-getrennter Konfigurations-Block für EVE-System-Prompts.
 * Wirkt 1:1 wie V1-Output.
 */
export function formatEveProfilePromptBlock(profile: EveProfile | null | undefined): string {
  if (!profile) return '';
  const toneLine = TONE_MAP[profile.tone] ?? TONE_MAP['direkt']!;
  const styles = profile.tradingStyles ?? [];
  const timeframes = profile.preferredTimeframes ?? [];
  const assets = parseAllAssets(profile);

  const lines = [
    `\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
    `EVE PROFIL-KONFIGURATION (vom User gesetzt — PFLICHT zu beachten):`,
    `TON: ${toneLine}`,
    `TRADING-STILE: ${styles.join(', ')}`,
    `BEVORZUGTE TIMEFRAMES: ${timeframes.join(', ')}`,
    `BEVORZUGTE ASSETS: ${assets.join(', ')}`,
    `MAX HEBEL: ${profile.maxLeverage}x | MAX RISIKO PRO TRADE: ${profile.riskPercent}%`,
    `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
  ];
  const free = (profile.freeContext ?? '').trim();
  if (free) {
    lines.push(
      `WAS DER USER ÜBER SICH SAGT (vom User gesetzt — beachte das):`,
      free,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
    );
  }
  return lines.join('\n');
}
